import { useEffect, useState } from "react";
import { t } from "../i18n";
import { Link } from "react-router-dom";
import { Cake, CalendarClock, MapPin, Map, Crown, AlertTriangle, Clock, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useModals } from "../context/ModalsContext";
import { Shell } from "../components/Shell";
import { Copiloto } from "../components/Copiloto";
import { Avatar, Flag, RedFlagIcon, RED } from "../components/Avatar";
import { fmtRango, tipoLabel } from "../lib/data";
import { Trend } from "./Ranking";

const Section = ({ title, to, children, testId }) => (
  <section data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <p className="label mb-0">{title}</p>
      {to && <Link to={to} className="text-xs text-[#f3c649] flex items-center">{t("common.seeAll")} <ChevronRight size={12} /></Link>}
    </div>
    {children}
  </section>
);
const Empty = ({ text }) => <p className="surface p-4 text-sm text-muted-foreground">{text}</p>;

export default function Inicio() {
  const { version } = useModals();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD({ proximas_citas: [], cumpleanos: [], top5: [], sin_contacto: [] })); }, [version]);

  return (
    <Shell title={t("appt.start")}>
      <div className="space-y-6 fade-up" data-testid="dashboard">
        <Copiloto />

        {!d && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
        {d && <>
          <Section title={t("home.upcoming")} to="/calendario" testId="dash-citas">
            {d.proximas_citas.length === 0 ? <Empty text={t("home.noUpcoming")} /> : (
              <ul className="space-y-2">{d.proximas_citas.map((c) => (
                <li key={c.id}><Link to="/calendario" data-testid={`dash-cita-${c.id}`} className={`surface p-3 flex items-center gap-3 ${c.tiene_cruce ? "border-[#f43f5e]/70" : ""}`} style={c.personas?.some((p) => p.red_flag) ? { borderStyle: "dashed", borderColor: `${RED}aa` } : undefined}>
                  <Avatar nombre={c.personas?.[0]?.nombre} src={c.personas?.[0]?.foto_url} size={44} redFlag={c.personas?.[0]?.red_flag} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2 truncate">{c.personas?.map((p) => p.nombre).join(", ")} <Flag iso={c.personas?.[0]?.pais_iso} size={14} />
                      {c.personas?.some((p) => p.red_flag) && <RedFlagIcon size={13} testId={`dash-cita-red-flag-${c.id}`} />}
                      {c.tiene_cruce && <span data-testid="dash-cruce" className="text-[10px] uppercase tracking-wider text-[#fb7185] flex items-center gap-1"><AlertTriangle size={11} /> {t("appt.clash")}</span>}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><CalendarClock size={11} /> <span data-testid={`dash-cita-rango-${c.id}`}>{fmtRango(c.inicio, c.fin)}</span>{(c.lugar?.nombre || c.lugar?.ciudad) && <> · <MapPin size={11} /> {[c.lugar.nombre, c.lugar.ciudad].filter(Boolean).join(", ")}</>}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{tipoLabel(c.tipo)}</span>
                </Link></li>))}</ul>)}
          </Section>

          <Section title={t("home.birthdays")} testId="dash-cumples">
            {d.cumpleanos.length === 0 ? <Empty text={t("home.noBirthdays")} /> : (
              <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4">{d.cumpleanos.map((p) => (
                <Link key={p.id} to={`/personas/${p.id}`} data-testid={`dash-cumple-${p.id}`} className="surface p-3 flex flex-col items-center gap-2 min-w-[110px]">
                  <Avatar nombre={p.nombre} src={p.foto_url} size={48} />
                  <p className="text-sm font-medium flex items-center gap-1">{p.nombre} <Flag iso={p.pais_iso} size={12} /></p>
                  <p className={`text-xs flex items-center gap-1 ${p.dias === 0 ? "text-[#f3c649]" : "text-muted-foreground"}`}><Cake size={11} /> {p.dias === 0 ? t("home.todayBang") : p.dias === 1 ? t("home.tomorrow") : t("home.inDays", { n: p.dias })}</p>
                </Link>))}</div>)}
          </Section>

          <Section title={t("home.top5")} to="/ranking" testId="dash-top5">
            {d.top5.length === 0 ? <Empty text={t("home.noActive")} /> : (
              <ul className="surface divide-y divide-border">{d.top5.map((p) => (
                <li key={p.id}><Link to={`/personas/${p.id}`} data-testid={`dash-top-${p.posicion}`} className="flex items-center gap-3 px-3 py-2">
                  <span className="w-5 text-center text-sm font-display text-muted-foreground">{p.posicion}</span>
                  <Avatar nombre={p.nombre} src={p.foto_url} size={32} coronada={p.coronada} redFlag={p.red_flag} />
                  <span className="flex-1 text-sm flex items-center gap-2 truncate">{p.nombre} <Flag iso={p.pais_iso} size={12} /> {p.coronada && <Crown size={12} className="text-[#d4af37]" fill="#d4af37" />}{p.red_flag && <RedFlagIcon size={12} testId={`dash-top-red-flag-${p.id}`} />}</span>
                  <span className="text-sm font-display text-[#f3c649]">{p.score.toFixed(1)}</span><Trend t={p.score_tendencia} />
                </Link></li>))}</ul>)}
          </Section>

          <Section title={t("home.reachOut")} to="/alertas" testId="dash-sin-contacto">
            {d.sin_contacto.length === 0 ? <Empty text={t("home.allGood")} /> : (
              <ul className="space-y-2">{d.sin_contacto.map((p) => (
                <li key={p.id}><Link to={`/personas/${p.id}`} data-testid={`dash-sincontacto-${p.id}`} className="surface p-3 flex items-center gap-3">
                  <Avatar nombre={p.nombre} src={p.foto_url} size={40} />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium flex items-center gap-2">{p.nombre} <Flag iso={p.pais_iso} size={12} /></p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} /> {p.nunca ? t("home.noContactEver") : t("home.daysNoContact", { n: p.dias })}</p></div>
                </Link></li>))}</ul>)}
          </Section>

          <Link to="/mapa" data-testid="dash-mapa" className="surface p-4 flex items-center gap-3 hover:bg-secondary transition-colors">
            <Map size={22} className="text-[#d4af37]" />
            <div className="flex-1"><p className="text-sm font-medium">{t("home.mapTitle")}</p><p className="text-xs text-muted-foreground">{t("home.mapSub")}</p></div>
            <ChevronRight size={16} className="text-muted-foreground" />
          </Link>
        </>}
      </div>
    </Shell>
  );
}
